import type { AddressableLine, Segment, SegmentPortfolioSummary } from "../types"
import type { LucideIcon } from "@/components/icons"

import { BarChart3, Layers3, PieChart, Swords, Target, TrendingUp, Users } from "@/components/icons"

import { COMPANY_NAME } from "./branding"

/**
 * User-selectable KPI card catalog (#2067 / hygiene #2421).
 * Catalog is intentionally small: score / pending / stale / highlight tiles removed.
 */
export type KpiCardSpec = {
  id: string
  icon: LucideIcon
  label: string
  subLabel: string
  value: string
}

export function formatKpiNumber(value: number, decimals = 0): string {
  return value.toLocaleString("en-US", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  })
}

function uniqueCount(segments: Segment[], pick: (segment: Segment) => string[]): number {
  const set = new Set<string>()
  for (const segment of segments) for (const name of pick(segment)) set.add(name)
  return set.size
}

/** Sum of all addressable-line totals in the visible portfolio. */
export function addressableMarketTotal(summary: SegmentPortfolioSummary): number {
  return Object.values(summary.addressableTotals).reduce((sum, value) => sum + (value || 0), 0)
}

/**
 * Build the full catalog in canonical display order. The selected subset keeps
 * this order, so the row stays stable regardless of the order boxes were ticked.
 */
export function buildKpiCatalog(input: {
  summary: SegmentPortfolioSummary
  segments: Segment[]
  addressableLines: AddressableLine[]
  pendingCounts: Record<string, number>
  /** Kept for API stability; stale tiles are no longer catalogued. */
  staleDays?: number
  now?: Date
}): KpiCardSpec[] {
  const { summary, segments, addressableLines } = input
  const amTotal = addressableMarketTotal(summary)

  const cards: KpiCardSpec[] = [
    {
      id: "company-sales",
      icon: TrendingUp,
      label: `${COMPANY_NAME} sales`,
      subLabel: "Estimate · updated manually",
      value: formatKpiNumber(summary.totalSales),
    },
    {
      id: "market-revenue",
      icon: BarChart3,
      label: "Market revenue",
      subLabel: "Total market, MEUR",
      value: formatKpiNumber(summary.totalMarket),
    },
    {
      id: "addressable-market",
      icon: Layers3,
      label: "Addressable market",
      subLabel: "Manual estimate, MEUR",
      value: amTotal > 0 ? formatKpiNumber(amTotal) : "—",
    },
  ]

  for (const line of addressableLines) {
    if ((summary.addressableTotals[line.key] ?? 0) <= 0) continue
    // Skip redundant per-line tile when it is the only line and equals the roll-up.
    if (addressableLines.length === 1) continue
    cards.push({
      id: `addressable:${line.key}`,
      icon: Layers3,
      label: line.label,
      subLabel: line.sublabel ?? "MEUR",
      value: formatKpiNumber(summary.addressableTotals[line.key] ?? 0),
    })
  }

  cards.push(
    {
      id: "market-share",
      icon: PieChart,
      label: "Market share",
      subLabel: `${COMPANY_NAME} sales / addressable market`,
      value: amTotal > 0 ? `${formatKpiNumber((summary.totalSales / amTotal) * 100, 1)}%` : "—",
    },
    {
      id: "avg-cagr",
      icon: Target,
      label: "Avg CAGR 5y",
      subLabel: "weighted by market revenue",
      value: `${formatKpiNumber(summary.averageGrowth, 1)}%`,
    },
    {
      id: "customers-tracked",
      icon: Users,
      label: "Customers",
      subLabel: "unique across segments in view",
      value: formatKpiNumber(uniqueCount(segments, (segment) => segment.customers)),
    },
    {
      id: "competitors-tracked",
      icon: Swords,
      label: "Competitors",
      subLabel: "unique across segments in view",
      value: formatKpiNumber(uniqueCount(segments, (segment) => segment.competitors)),
    }
  )

  return cards
}

/**
 * Default selection: sales, total market, addressable market, share-vs-AM, CAGR,
 * plus customer/competitor unique counts across the visible portfolio.
 */
export function defaultKpiCardIds(
  summary: SegmentPortfolioSummary,
  _addressableLines: AddressableLine[],
  view: "portfolio" | "companies" | "map" = "portfolio"
): string[] {
  if (view === "companies") {
    return ["customers-tracked", "competitors-tracked", "market-revenue"]
  }
  void summary
  return [
    "company-sales",
    "market-revenue",
    "addressable-market",
    "market-share",
    "avg-cagr",
    "customers-tracked",
    "competitors-tracked",
  ]
}

/** Fixed (data-independent) KPI card ids the product currently offers. */
const STATIC_KPI_CARD_IDS: ReadonlySet<string> = new Set([
  "company-sales",
  "market-revenue",
  "addressable-market",
  "market-share",
  "avg-cagr",
  "customers-tracked",
  "competitors-tracked",
])

const ADDRESSABLE_LINE_CARD_PREFIX = "addressable:"

/**
 * Whether a saved KPI card id is one the product still offers. Decided
 * statically: per-line `addressable:<key>` tiles are data-dependent in
 * `buildKpiCatalog` (absent when the line totals 0 in view, when the tenant has
 * a single line, or when the line is hidden), so their momentary absence from
 * the catalog must not mark a saved selection as stale.
 */
export function isKnownKpiCardId(id: string): boolean {
  return (
    STATIC_KPI_CARD_IDS.has(id) ||
    (id.startsWith(ADDRESSABLE_LINE_CARD_PREFIX) && id.length > ADDRESSABLE_LINE_CARD_PREFIX.length)
  )
}

/** Pick the user's cards from the catalog IN SELECTION ORDER (#2068 A1).
 *
 * A saved selection can reference ids the product no longer offers (e.g. #2461
 * dropped the score/pending/stale/highlight tiles): filtering those out
 * one-by-one silently shrinks a 7-tile selection to whatever survives (#3295
 * Nolato feedback #3). If the selection contains ANY id the product no longer
 * offers, treat the whole saved selection as stale and fall back to the
 * current defaults, rather than showing only the survivors. Ids that are still
 * valid but have no tile in the current (filtered) catalog are skipped
 * individually, keeping the rest of the user's customisation.
 */
export function selectKpiCards(
  catalog: KpiCardSpec[],
  selectedIds: string[] | undefined,
  defaults: string[]
): KpiCardSpec[] {
  const byId = new Map(catalog.map((card) => [card.id, card]))
  const ids = selectedIds?.length ? selectedIds : defaults
  const hasStaleId = ids.some((id) => !isKnownKpiCardId(id))
  const effectiveIds = hasStaleId ? defaults : ids
  const picked = effectiveIds
    .map((id) => byId.get(id))
    .filter((card): card is KpiCardSpec => Boolean(card))
  if (picked.length > 0) return picked
  return defaults.map((id) => byId.get(id)).filter((card): card is KpiCardSpec => Boolean(card))
}

/** Checklist entries for the Customize popover. */
export function listKpiCardOptions(
  addressableLines: AddressableLine[]
): Array<{ id: string; label: string }> {
  return [
    { id: "company-sales", label: `${COMPANY_NAME} sales` },
    { id: "market-revenue", label: "Market revenue" },
    { id: "addressable-market", label: "Addressable market" },
    ...addressableLines.map((line) => ({ id: `addressable:${line.key}`, label: line.label })),
    { id: "market-share", label: "Market share" },
    { id: "avg-cagr", label: "Avg CAGR 5y" },
    { id: "customers-tracked", label: "Customers" },
    { id: "competitors-tracked", label: "Competitors" },
  ]
}
